import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuthStore } from '@/stores/authStore'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { getSessionActivity, setSignedOutNotice, subscribeSessionActivity } from '@/lib/sessionActivity'
import { getErrorMessage } from '@/services/api/client'
import { isStaleIdentityError } from '@/services/api/identityEpoch'
import { isTransientAuthError } from '@/services/api/transientAuthError'

/** The warning opens this long before the server's idle sign-out. */
export const IDLE_WARNING_LEAD_MS = 60_000
/** How often the deadline is re-checked outside the warning. */
export const IDLE_CHECK_INTERVAL_MS = 5_000
/** Countdown tick while the warning is open. */
const COUNTDOWN_TICK_MS = 1_000

/**
 * Warns before the server signs the session out for inactivity, and offers
 * to stay signed in. Mounted once in the authenticated shell (Layout).
 *
 * The deadline comes from lib/sessionActivity (last login/refresh + the
 * server's idleTimeout), shared by every tab. Checking it reads localStorage
 * only: it never calls the server, so the warning itself is not activity.
 *
 * "Stay signed in" runs the store's single-flight refresh, which restarts the
 * window. When the deadline passes, ONE refresh is attempted: if the server
 * already ended the session it answers with its own inactivity 401, and the
 * store's normal failed-refresh sign-out runs; there is no separate sign-out
 * path here. The server's message is handed to the sign-in page.
 *
 * No idle window known (dev-mode tokens, SSO sessions) means no warning.
 */
export function IdleWarning() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const refreshSession = useAuthStore((s) => s.refreshSession)
  const logout = useAuthStore((s) => s.logout)

  const [activity, setActivity] = useState(getSessionActivity)
  const [now, setNow] = useState(() => Date.now())
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // The window (by its start) the user closed the warning for, and the one an
  // expiry refresh was already attempted for: neither repeats for that window.
  const [dismissedFor, setDismissedFor] = useState<number | null>(null)
  const expiredFor = useRef<number | null>(null)

  const deadline = activity ? activity.lastActivityAt + activity.idleTimeoutMs : null
  const remaining = deadline === null ? null : deadline - now
  const inWarning = isAuthenticated && remaining !== null && remaining > 0 && remaining <= IDLE_WARNING_LEAD_MS
  const open = inWarning && dismissedFor !== activity?.lastActivityAt

  // Another tab's refresh (storage event) or this tab's own record updates.
  useEffect(
    () =>
      subscribeSessionActivity(() => {
        setActivity(getSessionActivity())
        setNow(Date.now())
        setError(null)
      }),
    [],
  )

  // Re-check the deadline: every few seconds, every second inside the warning.
  useEffect(() => {
    if (!isAuthenticated) return
    const id = window.setInterval(
      () => {
        setActivity(getSessionActivity())
        setNow(Date.now())
      },
      inWarning ? COUNTDOWN_TICK_MS : IDLE_CHECK_INTERVAL_MS,
    )
    return () => window.clearInterval(id)
  }, [isAuthenticated, inWarning])

  const runRefresh = useCallback(async (): Promise<void> => {
    setBusy(true)
    setError(null)
    try {
      // On success the store records the new window and the dialog closes.
      await refreshSession()
    } catch (err) {
      if (isStaleIdentityError(err)) return
      if (isTransientAuthError(err)) {
        // The session may still be live; the store kept it. Say so and let
        // the user retry while there is time.
        setError("Couldn't reach the server to keep you signed in. Try again.")
        return
      }
      // The server refused: the store has already signed this tab out and the
      // signed-out redirect follows. Carry the server's reason to the sign-in page.
      const message = getErrorMessage(err, 'Your session has ended. Please sign in again.')
      setSignedOutNotice(message)
      setError(message)
    } finally {
      setBusy(false)
    }
  }, [refreshSession])

  // Deadline passed: one refresh, so the server's answer decides.
  useEffect(() => {
    if (!isAuthenticated || !activity || remaining === null || remaining > 0) return
    if (expiredFor.current === activity.lastActivityAt) return
    expiredFor.current = activity.lastActivityAt
    void runRefresh()
  }, [isAuthenticated, activity, remaining, runRefresh])

  const handleSignOut = async () => {
    const { serverRevoked } = await logout()
    window.location.assign(serverRevoked ? '/login' : '/login?reason=signout_incomplete')
  }

  if (!isAuthenticated || !activity) return null

  const seconds = Math.max(0, Math.ceil((remaining ?? 0) / 1000))

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && activity) setDismissedFor(activity.lastActivityAt)
      }}
    >
      <DialogContent onInteractOutside={(e) => e.preventDefault()} data-testid="idle-warning">
        <DialogHeader>
          <DialogTitle>Still there?</DialogTitle>
          <DialogDescription aria-live="polite" aria-atomic="true">
            You'll be signed out in {seconds} {seconds === 1 ? 'second' : 'seconds'} because of inactivity.
          </DialogDescription>
        </DialogHeader>
        {error && (
          <DialogBody>
            <p role="alert" className="text-sm text-red-700 dark:text-red-400">
              {error}
            </p>
          </DialogBody>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => void handleSignOut()} disabled={busy}>
            Sign out
          </Button>
          <Button type="button" onClick={() => void runRefresh()} loading={busy} disabled={busy}>
            Stay signed in
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
